import {
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  Input,
  NgZone,
  OnInit,
  signal,
  Signal,
  ViewChild
} from '@angular/core';
import { BaseFormControlComponent } from '../../../classes/base-form-control.component';
import {
  ChecklistQuestionCategory,
  ChecklistQuestionGroup,
  ChecklistsQuestion
} from './interfaces/checklists-question.interface';
import { ChecklistQuestionComponent } from 'src/app/features/bizzmine/form/components/controls/execute-checklist-control/components/question/question.component';
import { ChecklistSidebarComponent } from 'src/app/features/bizzmine/form/components/controls/execute-checklist-control/components/sidebar/sidebar.component';
import { ChecklistProgressbarComponent } from 'src/app/features/bizzmine/form/components/controls/execute-checklist-control/components/progressbar/progressbar.component';
import { ChecklistGroupsComponent } from './components/checklist-groups/checklist-groups.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DocumentEventsService } from 'src/app/shared/services/document-events/document-events.service';
import { BehaviorSubject, debounceTime, distinctUntilChanged, filter, take } from 'rxjs';
import { formsActions } from '../../../../../../store/features/forms/forms-actions';
import {
  selectForm,
  selectFormField,
  selectFormLockState
} from '../../../../../../store/features/forms/forms-selectors';
import { ChecklistAnswer, ChecklistApiService } from 'src/app/api/bizzmine/checklist/checklist.service';
import { AlertService } from 'src/app/features/bizzmine/alerts/alert.service';
import { isQuestionChecked } from 'src/app/features/bizzmine/form/components/controls/execute-checklist-control/functions/question-valid';
import { BreakpointEnum } from 'src/app/shared/enums/pixel-breakpoints';
import { IconComponent } from 'src/app/shared/components/ui/icon/icon.component';
import { TranslatePipe } from 'src/app/shared/pipes/translate/translate.pipe';
import { LoaderButtonComponent } from 'src/app/shared/components/loader-button/loader-button/loader-button.component';
import { ChecklistSearchbarComponent } from './components/searchbar/searchbar.component';
import { AsyncPipe, CommonModule } from '@angular/common';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { CollectionFormField } from '../../../../../../../models/ts/collection-form-field.model';
import { Actions, ofType } from '@ngrx/effects';

@Component({
  selector: 'bizz-execute-checklist-control',
  standalone: true,
  imports: [
    ChecklistQuestionComponent,
    ChecklistSidebarComponent,
    ChecklistProgressbarComponent,
    ChecklistGroupsComponent,
    ChecklistSearchbarComponent,
    IconComponent,
    TranslatePipe,
    LoaderButtonComponent,
    AsyncPipe,
    CommonModule

  ],
  templateUrl: './execute-checklist-control.component.html',
  styleUrl: './execute-checklist-control.component.scss'
})
export class ExecuteChecklistControlComponent extends BaseFormControlComponent implements OnInit, AfterViewInit {
  @Input() public override formFieldSignal: Signal<CollectionFormField | undefined> = signal<CollectionFormField | undefined>(undefined);
  @Input() public override formId: string;
  @Input() public override reportControl: boolean = false;
  public currentQuestionIndex: number = 0;
  public questions: ChecklistsQuestion[];
  public filteredQuestions: ChecklistsQuestion[];
  public selectedQuestion: ChecklistsQuestion[] = [];
  public isSaveQuestionLoading: boolean = false;
  public checkAllQuestions: boolean = true;

  public selectedGroup: { group: ChecklistQuestionGroup, questions: number[], checkedQuestions: number } | null;
  public defaultGroupSet: boolean = false;
  public checkedQuestions: number = 0;
  public isLocked = computed(() => {
    if (this.formId != null) {
      return this.store$.selectSignal(selectFormLockState(this.formId))();
    }
    return false;
  });

  public isReadonly = computed(() => {
    return this.formFieldSignal()?.IsReadOnly ?? false;
  });

  public isRequired = computed(() => {
    return this.formFieldSignal()?.IsRequired ?? false;
  });

  public groups$ = new BehaviorSubject<{
    group: ChecklistQuestionGroup,
    questions: number[],
    checkedQuestions: number
  }[] | undefined>(undefined);
  private questions$ = new BehaviorSubject<ChecklistsQuestion[] | undefined>(undefined);
  public categories: Array<ChecklistQuestionCategory>;

  public getScreenWidth: number = window.innerWidth;
  public BreakpointEnum = BreakpointEnum;
  public isMobileView: boolean = false;
  public showSearch: boolean = false;
  public showCategories: boolean = false;
  @ViewChild('searchInput') public searchInput: ChecklistSearchbarComponent;
  @ViewChild('questionElem', { read: ElementRef }) private questionElem: ElementRef;
  private intersectionObserver: IntersectionObserver;
  public showFooter: boolean = false;
  public isDarkTheme: boolean = false;

  public constructor(
    private documentEventsService: DocumentEventsService,
    private checklistApiService: ChecklistApiService,
    private alertService: AlertService,
    private actions: Actions,
    private ngZone: NgZone,
    private themeService: ThemeService) {
    super();
    this.documentEventsService.isMobile.pipe(
      takeUntilDestroyed(this.destroyRef)).subscribe((isMobile: boolean) => {
        this.isMobileView = isMobile;
        if (isMobile) {
          if (!this.intersectionObserver) this.questionIntersectionObserver();
          this.hidePopups();
        }
      }
    );

    this.documentEventsService.windowResizeEvent.pipe(takeUntilDestroyed(this.destroyRef),
      debounceTime(300))
      .subscribe((e) => {
        this.getScreenWidth = window.innerWidth;
      });

    this.themeService.currentTheme$.pipe(take(1)).subscribe((theme) => {
      if (theme) {
        this.isDarkTheme = theme.IsDarkTheme;
      }
    });

    this.initQuestionsDispatchListener();
  }

  private initQuestionsDispatchListener(): void {
    this.questions$.pipe(
      filter(questions => questions != undefined),
      debounceTime(300),
      distinctUntilChanged((prev, curr) => {
        return JSON.stringify(prev) == JSON.stringify(curr);
      }),
      takeUntilDestroyed(this.destroyRef))
      .subscribe(
        {
          next: (values: ChecklistsQuestion[]) => {
            this.store$.dispatch(formsActions.updateFormFieldValue({
              formId: this.formId,
              value: values,
              fieldId: this.formFieldSignal()?.Id ?? 0
            }));
          }
        }
      );
  }

  public override ngAfterViewInit(): void {
    if (this.isMobileView) this.questionIntersectionObserver();
  }

  private questionIntersectionObserver(): void {
    this.intersectionObserver = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        this.showFooter = true;
      } else {
        this.showFooter = false;
      }
    }, { threshold: [0.0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0] });

    this.intersectionObserver.observe(this.questionElem.nativeElement);
  }

  public ngOnDestroy(): void {
    this.intersectionObserver?.disconnect();
  }

  public override ngOnInit(): void {
    //check if questions are answered when the form is reset
    this.actions.pipe(takeUntilDestroyed(this.destroyRef),
      ofType(formsActions.updateForm),
      filter(event => event.update?.changes?.reset == true))
      .subscribe(() => {
        this.checkAllQuestions = true;
        this.initQuestions(this.questions);
      });

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.store$.select(selectFormField(this.formId, this.formFieldId()!))
      .pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged((prev, curr) => {
        return JSON.stringify(prev?.Value) == JSON.stringify(curr?.Value);
      }))
      .subscribe(questionsField => {
        if (questionsField) {
          let questions = structuredClone(questionsField.Value ?? []) as ChecklistsQuestion[];
          this.initQuestions(questions);
        }
      });
  }

  /**
   * Initializes the questions for the checklist control.
   * Sets the questions and groups them and sets the checked state.
   * Also sorts the questions by group and order ID.
   * @param {ChecklistsQuestion[]} questions
   * @private
   */
  private initQuestions(questions: ChecklistsQuestion[]): void {
    questions.forEach(el => {
      el.UserAnswer = el.UserAnswer ?? {};
    });

    this.questions = questions;

    questions.sort((a, b) => {
      if (a.Group.GroupVersionsID === b.Group.GroupVersionsID) {
        return a.OrderID - b.OrderID;
      }
      return a.Group.OrderID - b.Group.OrderID;
    });

    if (this.checkAllQuestions) {
      questions = this.setQuestionsChecked(questions);
      this.currentQuestionIndex = questions.findIndex((question) => !question.isChecked);
      this.checkAllQuestions = false;
    }
    this.checkedQuestions = questions.filter((question) => question.isChecked).length;

    this.questions = questions;
    this.filteredQuestions = questions;

    const questionsGroupedByGroup = this.getQuestionsGroupedByGroup(questions);
    if (!this.defaultGroupSet) {
      this.setActiveGroup(questionsGroupedByGroup[0]);
      this.defaultGroupSet = true;
    }
    this.groups$.next(questionsGroupedByGroup);
  }

  private setQuestionCollapsedStates(versionsId: number, isCollapsed: boolean): void {
    this.questions.forEach((question) => {
      if (question.VersionsID == versionsId) {
        question.isCollapsed = isCollapsed;
      }
    });
  }

  private resetQuestionCollapsedStates(): void {
    this.questions.forEach((question) => {
      question.isCollapsed = true;
    });
  }

  public questionClicked(questionIndex: number, isCollapsed: boolean): void {
    if (!isCollapsed) {
      const prevQuestion = this.questions[this.currentQuestionIndex];
      if (prevQuestion) {
        this.saveQuestion(prevQuestion, isCollapsed);
      }
    }

    this.setcurrentQuestionIndex(questionIndex);

    if (this.isMobileView) {
      this.hidePopups();
    }
    const currentQuestion = this.questions[this.currentQuestionIndex];
    if (currentQuestion) {
      this.setQuestionCollapsedStates(currentQuestion.VersionsID, isCollapsed);

      this.setActiveGroupBasedOnQuestion(currentQuestion);

      this.scrollToElement('question_' + this.currentQuestionIndex);
    }
  }

  private setActiveGroupBasedOnQuestion(currentQuestion: ChecklistsQuestion): void {
    if (currentQuestion.Group) {
      const category = this.groupsValue.find((question) => question.group.GroupVersionsID === currentQuestion.Group.GroupVersionsID);
      if (category) {
        this.setActiveGroup(category);
      }
    }
  }

  public get groupsValue(): { group: ChecklistQuestionGroup, questions: number[], checkedQuestions: number }[] {
    return this.groups$.value as { group: ChecklistQuestionGroup, questions: number[], checkedQuestions: number }[];
  }

  public filterChange(selectedQuestion: ChecklistsQuestion): void {
    if (selectedQuestion) {
      selectedQuestion.isCollapsed = false;
      this.currentQuestionIndex = this.questions.findIndex((question) => question.VersionsID === selectedQuestion.VersionsID);
      if (selectedQuestion.Group) {
        const category = this.groupsValue.find((question) => question.group.GroupVersionsID === selectedQuestion.Group.GroupVersionsID);
        if (category) {
          this.setActiveGroup(category);
        }
      }
      this.scrollToElement('question_' + this.currentQuestionIndex);
    }
  }

  public groupChanged(category: {
    group: ChecklistQuestionGroup,
    questions: number[],
    checkedQuestions: number
  }): void {
    this.setActiveGroup(category);
    this.resetQuestionCollapsedStates();
    this.scrollToElement(category.group.GroupVersionsID.toString());
  }

  private getQuestionsGroupedByGroup(questions: ChecklistsQuestion[]): {
    group: ChecklistQuestionGroup,
    questions: number[],
    checkedQuestions: number
  }[] {
    const map = new Map();
    questions.forEach((question: ChecklistsQuestion, index) => {
      if (!map.has(question.Group.GroupVersionsID)) {
        map.set(question.Group.GroupVersionsID, { group: question.Group, questions: [], checkedQuestions: 0 });
      }
      const cat = map.get(question.Group.GroupVersionsID);
      if (cat) {
        cat.questions.push(index);
        if (question.isChecked) {
          cat.checkedQuestions++;
        }
      }
    });
    return Array.from(map.values());
  }

  private setQuestionsChecked(questions: ChecklistsQuestion[]): ChecklistsQuestion[] {
    questions.forEach((question) => {
      question.isChecked = isQuestionChecked(question);
    });
    return questions;
  }

  private setActiveGroup(category: {
    group: ChecklistQuestionGroup,
    questions: number[],
    checkedQuestions: number
  }): void {
    this.selectedGroup = category;
  }

  private setcurrentQuestionIndex(index: number): void {
    this.currentQuestionIndex = index;
  }

  public dispatchChange(event: ChecklistsQuestion): void {
    // event not yet used -> could be used to typecheck e.g.: if canvas -> upload or what ever
    this.questions$.next(this.questions);
  }

  public saveQuestion(question: ChecklistsQuestion, setNextQuestion: boolean = true): void {
    if (question.isAnswered) {
      this.postChecklistQuestion(question, setNextQuestion);
    } else {
      if (setNextQuestion) {
        this.setNextQuestionIndex();
      }

      const currentQuestion = this.questions[this.currentQuestionIndex];
      if (currentQuestion) {
        this.setActiveGroupBasedOnQuestion(currentQuestion);
        this.scrollToElement('question_' + this.currentQuestionIndex);
        this.questions[this.currentQuestionIndex].isCollapsed = false;
      }
    }
  }

  private postChecklistQuestion(question: ChecklistsQuestion, setNextQuestion: boolean = true): void {
    const userAnswer = {
      QuestionVersionsID: question.VersionsID,
      ResultClassID: question.ResultClass.ID,
      ResultClassValueID: question.UserAnswer.ResultClassValueID,
      Remarks: question.UserAnswer.Remarks ?? '',
      Attachments: question.UserAnswer.Attachments ?? [],
      Value: question.UserAnswer.Value ?? ''
    } as ChecklistAnswer;
    const form = this.store$.selectSignal(selectForm(this.formId))()?.data;
    this.isSaveQuestionLoading = true;

    this.checklistApiService.postChecklistQuestion(
      this.formFieldSignal()?.DataDesignViewDataSourcesID ?? 0,
      form?.VersionsID ?? 0,
      userAnswer).pipe(take(1)).subscribe(
      {
        next: () => {
          this.isSaveQuestionLoading = false;
          question.isChecked = isQuestionChecked(question); //TODO: check if this is correct

          if (setNextQuestion) {
            this.setNextQuestionIndex();
          }

          const currentQuestion = this.questions[this.currentQuestionIndex];
          if (currentQuestion) {
            this.setActiveGroupBasedOnQuestion(currentQuestion);
            this.scrollToElement('question_' + this.currentQuestionIndex);
          }

          this.dispatchChange(question);
        },
        error: () => {
          this.alertService.setAlert({
            type: 'error',
            title: 'Error while saving the question. Please try again later.',
            content: [],
            icon: 'exclamation-triangle',
            dismissable: true,
            timed: true,
            timer: 3000
          });
          this.isSaveQuestionLoading = false;
        }
      }
    );
  }

  private setNextQuestionIndex(): void {
    const nextQuestionIndex = this.questions.findIndex((question, index) => index > this.currentQuestionIndex && !question.isChecked);
    if (nextQuestionIndex !== -1) {
      this.currentQuestionIndex = nextQuestionIndex;
    } else {
      this.currentQuestionIndex = this.questions.findIndex((question) => !question.isChecked);
    }

  }

  private scrollToElement(id: string): void {
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        const element = document.getElementById(this.formId + '_' + id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
        }
      }, 0);
    });
  }

  public hidePopups(): void {
    this.showCategories = false;
    this.showSearch = false;
  }

  public toggleShowSearch(): void {
    this.showSearch = !this.showSearch;
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        if (this.showSearch) this.searchInput.focus();
      }, 0);
    });
    this.showCategories = false;
  }

  public toggleShowCategories(): void {
    this.showCategories = !this.showCategories;
    this.showSearch = false;
  }

  protected override focus(): void {
    //throw new Error('Method not implemented.');
  }


}
